import { Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { ROUTES } from '@/constants/routes'
import { useNavigationGroups } from '@/hooks/useNavigationGroups'
import { useContextualNav } from '@/hooks/useContextualNav'
import { useCurrentEntity } from '@/contexts/EntityContext'
import { useInboxCounts, unreadMessagesBadgeCount, bottomTabBadgesFor } from '@/hooks/useInboxCounts'
import { useNotificationChannel } from '@/hooks/useNotificationChannel'
import { useDarkMode } from '@/hooks/useDarkMode'
import { AppShell } from '@/components/shell'
import type { AccessRoleName } from '@/components/shell/UserMenu'

function mapRole(role: string): AccessRoleName {
  if (role === 'platform_admin') return 'admin'
  return 'standard_user'
}

export function AuthenticatedLayout() {
  const { user, permissions, isAuthenticated, isRestoring, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const isCircleManager = user?.isCircleManager || user?.isMafaoEmployee || false
  // The B2B ops console requires a Mafao employee record or platform admin
  // (matches the `/api/v1/b2b` backend gate), which is stricter than the
  // Circle-Manager role that unlocks "My Relationships".
  const canAccessB2b = user?.isMafaoEmployee || user?.role === 'platform_admin' || false
  const { counts, refresh: refreshCounts } = useInboxCounts()
  // Real-time notification updates via ActionCable
  useNotificationChannel({ onNotification: () => refreshCounts() })
  const badges = {
    '/messages': unreadMessagesBadgeCount(counts),
    '/events': counts.pendingAcknowledgments,
  }
  // Bottom tab bar uses its own keys ("associations", "circles") rather than
  // routes; bottomTabBadgesFor() maps the inbox counts onto those keys so the
  // mobile design's notification dots fire on the correct tabs (see RT-02).
  const bottomTabBadges = bottomTabBadgesFor(counts)
  const navigationGroups = useNavigationGroups(permissions, { isCircleManager, canAccessB2b, badges })
  const { currentEntity } = useCurrentEntity()
  const contextualNav = useContextualNav({
    associationName: currentEntity?.type === 'association' ? currentEntity.name : null,
    circleName: currentEntity?.type === 'circle' ? currentEntity.name : null,
  })
  const { mode, toggle: toggleTheme } = useDarkMode()

  // E2 — while the session is still restoring (async token hydration + /auth/me
  // on a fresh load), hold on a loading screen rather than bouncing to /login;
  // otherwise a deep-link reload loses its destination.
  if (isRestoring) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" role="status" aria-label="Loading" />
      </div>
    )
  }

  if (!isAuthenticated) {
    // Preserve the intended location so the login flow can return here (E2).
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  }

  const shellUser = user
    ? {
        name: user.fullName,
        email: user.email,
        avatarUrl: user.avatarUrl || undefined,
        role: user.role,
        accessRole: mapRole(user.role),
        contextualRoles: user.associationRoles?.map((ar) => ({
          label: ar.role ? ar.role.charAt(0).toUpperCase() + ar.role.slice(1) : 'Member',
          scope: 'association',
          scopeName: ar.associationName,
        })),
      }
    : undefined

  return (
    <AppShell
      navigationGroups={navigationGroups}
      contextualNav={contextualNav}
      user={shellUser}
      onNavigate={(href) => navigate(href)}
      onCloseContextual={() => {
        // Navigate back to the parent list view
        const parentPath = contextualNav?.entityType === 'association' ? '/associations' : '/circles'
        navigate(parentPath)
      }}
      onLogout={logout}
      onSettings={() => navigate(ROUTES.SETTINGS)}
      onHelp={() => navigate(ROUTES.HELP)}
      onPermissions={() => navigate(ROUTES.ADMIN)}
      onViewProfile={() => navigate(ROUTES.PROFILE)}
      themeMode={mode}
      onThemeToggle={toggleTheme}
      bottomTabBadges={bottomTabBadges}
      isCircleManager={isCircleManager}
    >
      <Outlet />
    </AppShell>
  )
}
